import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiClient } from '@services/api'

interface InvoiceRow {
  id: number
  invoiceNo: string
  invoiceDate: string
  customerName: string
  channel: string
  itemCount: number
  taxableAmount: number
  gstRate: number
  gstAmount: number
  amount: number
  amountPaid: number
  balance: number
  type: 'Cash' | 'Credit' | 'UPI'
  paymentStatus: 'Paid' | 'Partial' | 'Unpaid' | 'Cancelled' | 'Draft'
  dueDate: string | null
  overdueDays: number
  createdBy: string
}

const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const formatDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
const statusBadge: Record<string, string> = { Paid: 'badge-full', Partial: 'badge-partial', Unpaid: 'badge-unpaid', Cancelled: 'badge-draft', Draft: 'badge-draft' }

export function InvoicesList() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [type, setType] = useState('')
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  if (status) params.set('status', status)
  if (type) params.set('type', type)
  const qs = params.toString()

  const { data, isLoading } = useQuery({ queryKey: ['invoices-list', qs], queryFn: () => apiClient.get<any>(`/invoices${qs ? `?${qs}` : ''}`) })
  const rows: InvoiceRow[] = data?.data?.items || []
  const summary = data?.data?.summary

  return (
    <div>
      <div className="hdr">
        <div className="field-label-row">
          <div>
            <h1>Sales Invoices</h1>
            <p>Every invoice generated from Billing — GST breakup, collections and what is still owed.</p>
          </div>
          <Link to="/dashboard/sales/invoices" className="btn-accent">+ New Sale</Link>
        </div>
      </div>

      {summary && (
        <div className="kpis">
          <div className="kpi"><div className="label">Invoices</div><div className="val">{summary.invoices}</div><div className="sub">{formatINR(summary.taxable)} taxable · {formatINR(summary.gst)} GST</div></div>
          <div className="kpi"><div className="label">Invoice value</div><div className="val">{formatINR(summary.amount)}</div><div className="sub">Cash/UPI {formatINR(summary.cash)} · Credit {formatINR(summary.credit)}</div></div>
          <div className="kpi"><div className="label">Collected</div><div className="val">{formatINR(summary.collected)}</div><div className="sub">posted to Collections</div></div>
          <div className="kpi"><div className="label">Outstanding</div><div className={`val ${summary.outstanding > 0 ? 'text-danger' : ''}`}>{formatINR(summary.outstanding)}</div><div className="sub">open on credit ledgers</div></div>
        </div>
      )}

      <div className="card">
        <div className="row3">
          <div><label>Search</label><input placeholder="Invoice no. or store" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
          <div>
            <label>Payment status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option><option>Paid</option><option>Partial</option><option>Unpaid</option><option>Draft</option><option>Cancelled</option>
            </select>
          </div>
          <div>
            <label>Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">All</option><option>Cash</option><option>Credit</option><option>UPI</option>
            </select>
          </div>
        </div>
        <table>
          <thead>
            <tr><th>Invoice</th><th>Date</th><th>Store / Customer</th><th>Channel</th><th className="num">Items</th><th className="num">Taxable</th><th className="num">GST</th><th className="num">Total</th><th className="num">Paid</th><th className="num">Balance</th><th>Type</th><th>Status</th></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={12} className="text-ink-soft">Loading…</td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={12} className="text-ink-soft">No invoices match.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id}>
                <td><Link to={`/dashboard/sales/invoices/${r.id}`} className="font-semibold text-primary-500 hover:underline">{r.invoiceNo}</Link></td>
                <td>{formatDate(r.invoiceDate)}</td>
                <td>{r.customerName}</td>
                <td>{r.channel}</td>
                <td className="num">{r.itemCount}</td>
                <td className="num">{formatINR(r.taxableAmount)}</td>
                <td className="num">{formatINR(r.gstAmount)} <span className="text-ink-soft text-[11px]">({r.gstRate}%)</span></td>
                <td className="num font-semibold">{formatINR(r.amount)}</td>
                <td className="num">{formatINR(r.amountPaid)}</td>
                <td className={`num ${r.balance > 0 ? 'text-danger font-semibold' : ''}`}>{formatINR(r.balance)}{r.overdueDays > 0 ? <div className="text-[11px] text-danger">{r.overdueDays}d overdue</div> : null}</td>
                <td><span className={`badge ${r.type === 'Credit' ? 'badge-credit' : 'badge-cash'}`}>{r.type}</span></td>
                <td><span className={`badge ${statusBadge[r.paymentStatus]}`}>{r.paymentStatus}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
